/**
 * Concentration Risk Page (/monitoring/concentration)
 *
 * Displays the institution's active risk-limit buckets and their open breaches.
 * Data source: GET /api/admin/risk/concentration
 *
 * What the read measures, per risk-concentration.routes.ts:
 *   - `current` is the currentValue of the bucket's most recent OPEN breach,
 *     recorded when the breach was detected, and `breachedAt` is when that
 *     breach was detected. A bucket with no open breach has no measured
 *     exposure, and the contract sends `current` null for it (ruling
 *     absence-representable-contracts). The page labels the column "Exposure
 *     at last open breach", prints '--' for a null current, and draws no
 *     utilization bar for it.
 *   - `status` is RED for an open high or critical breach, YELLOW for an open
 *     low or medium breach, and UNKNOWN for a bucket with no open breach,
 *     labeled "Not measured" in a neutral tone. GREEN is compared from a
 *     measured exposure only; it stays labeled "No open breach", never
 *     "within all thresholds".
 *
 * Critical path steps: 6 (Consumer sees capacity), 10 (Aaim monitors collateral)
 */

import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Card,
  DataTable,
  MetricCard,
  MetricCardGrid,
  METRIC_READ_CAPTIONS,
  Skeleton,
  formatDateTime,
  type DataTableColumn,
} from '@aaimplatform/ui';
import { riskConcentrationApi, type ConcentrationRiskItem } from '../../api/services/risk-concentration';
import { formatPercent, formatUsd, labelFor, RISK_LIMIT_LABELS } from './display-format';
import { readOutcome, refreshFailed, type ReadOutcome } from './read-state';
import { ReadNotice } from './ReadNotice';

/** One vocabulary for the status badge and the KPI cards. */
const BUCKET_STATUS_LABELS: Record<ConcentrationRiskItem['status'], string> = {
  GREEN: 'No open breach',
  YELLOW: 'Warning',
  RED: 'Breach',
  UNKNOWN: 'Not measured',
};

const BUCKET_STATUS_VARIANT: Record<ConcentrationRiskItem['status'], 'neutral' | 'warning' | 'error'> = {
  GREEN: 'neutral',
  YELLOW: 'warning',
  RED: 'error',
  UNKNOWN: 'neutral',
};

/** `CONCENTRATION / ASSET_CLASS` -> `Concentration / Asset class`. */
function bucketLabel(bucket: string): string {
  return bucket
    .split(' / ')
    .map((part) => labelFor(part, RISK_LIMIT_LABELS))
    .join(' / ');
}

/**
 * The exposure the read measured for this bucket, or null when it measured
 * none: the contract sends `current` null for a bucket with no open breach.
 */
function measuredExposure(row: ConcentrationRiskItem): number | null {
  return typeof row.current === 'number' ? row.current : null;
}

/**
 * The color of an open-breach count, by the status of the bucket(s) it counts.
 * Red is the alert color, so it marks only a count that includes a breach; a
 * count of warning-level breaches is amber, and zero is plain text.
 */
function openBreachCountClass(count: number, status: ConcentrationRiskItem['status']): string {
  if (count <= 0) return 'text-secondary';
  if (status === 'RED') return 'text-error font-semibold';
  if (status === 'YELLOW') return 'text-warning font-semibold';
  return 'text-secondary';
}

function UtilizationBar({ current, limit }: { current: number; limit: number }) {
  if (!(limit > 0)) {
    return <span className="font-mono text-xs text-tertiary">--</span>;
  }
  const pct = (current / limit) * 100;
  const color =
    pct >= 100
      ? 'rgb(var(--color-error))'
      : pct >= 80
        ? 'rgb(var(--color-warning))'
        : 'rgb(var(--color-success))';
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2 bg-recessed rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }}
        />
      </div>
      <span className="font-mono text-xs text-secondary">{formatPercent(pct)}</span>
    </div>
  );
}

const CONCENTRATION_COLUMNS: DataTableColumn<ConcentrationRiskItem>[] = [
  {
    header: 'Bucket',
    accessor: 'bucket',
    align: 'left',
    cell: (value) => (
      <span className="font-sans text-sm font-medium">{bucketLabel(String(value))}</span>
    ),
  },
  {
    header: 'Exposure at Last Open Breach',
    accessor: 'current',
    align: 'right',
    monospace: true,
    cell: (_value, row) => {
      const exposure = measuredExposure(row);
      return exposure == null ? (
        <span className="text-tertiary">--</span>
      ) : (
        formatUsd(exposure)
      );
    },
  },
  {
    header: 'Limit',
    accessor: 'limit',
    align: 'right',
    monospace: true,
    cell: (value) => formatUsd(Number(value)),
  },
  {
    header: 'Utilization at Breach',
    accessor: 'current',
    align: 'left',
    cell: (_value, row) => {
      const exposure = measuredExposure(row);
      return exposure == null ? (
        <span className="font-mono text-xs text-tertiary">--</span>
      ) : (
        <UtilizationBar current={exposure} limit={row.limit} />
      );
    },
  },
  {
    header: 'Last Breach Detected',
    accessor: 'breachedAt',
    align: 'left',
    cell: (_value, row) =>
      row.breachedAt ? (
        <span className="font-mono text-xs text-secondary">{formatDateTime(row.breachedAt, { seconds: false })}</span>
      ) : (
        <span className="font-mono text-xs text-tertiary">--</span>
      ),
  },
  {
    header: 'Open Breaches',
    accessor: 'breachCount',
    align: 'right',
    monospace: true,
    // The count takes its row's status color, so a warning-level breach is
    // amber beside its amber Warning badge. Red marks only a bucket in breach.
    cell: (value, row) => {
      const count = Number(value);
      return (
        <span className={openBreachCountClass(count, row.status)}>
          {count}
        </span>
      );
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    align: 'left',
    cell: (_value, row) => (
      <Badge variant={BUCKET_STATUS_VARIANT[row.status] ?? 'neutral'}>
        {BUCKET_STATUS_LABELS[row.status] ?? labelFor(String(row.status))}
      </Badge>
    ),
  },
];

const SUBJECT = 'Concentration risk data';

function sectionMessage(outcome: ReadOutcome): string {
  if (outcome === 'failed') return `${SUBJECT} could not be read.`;
  if (outcome !== 'read') return `${SUBJECT} not yet read.`;
  return 'No active risk limits are configured for your institution.';
}

export function ConcentrationRiskPage() {
  const query = useQuery({
    queryKey: ['admin', 'risk', 'concentration'],
    queryFn: () => riskConcentrationApi.getConcentration(),
    refetchInterval: 60000,
  });
  const outcome = readOutcome(query);

  // Counts exist only where a read succeeded; a failed or unread first read
  // prints '--' with the reason, never a count of zero buckets.
  const data = outcome === 'read' ? query.data : undefined;
  const buckets = data?.buckets ?? [];
  const notMeasuredBuckets = buckets.filter((b) => b.status === 'UNKNOWN').length;
  const warningBuckets = buckets.filter((b) => b.status === 'YELLOW').length;
  const breachBuckets = buckets.filter((b) => b.status === 'RED').length;
  const totalOpenBreaches = buckets.reduce((sum, b) => sum + b.breachCount, 0);

  const withheldCaption =
    outcome === 'failed'
      ? METRIC_READ_CAPTIONS.failed
      : outcome === 'read'
        ? undefined
        : METRIC_READ_CAPTIONS.unread;
  const count = (value: number): string => (data ? String(value) : '--');
  const note = (text: string): string | undefined => (data ? text : undefined);

  // Red is the alert color, so it marks only a bucket in breach. A warning-only
  // count is amber, and a zero is plain text: zero open breaches is not a
  // health verdict on exposure the read did not measure.
  const breachingClass =
    breachBuckets > 0 ? 'text-error' : warningBuckets > 0 ? 'text-warning' : 'text-secondary';
  const openBreachClass =
    breachBuckets > 0 ? 'text-error' : totalOpenBreaches > 0 ? 'text-warning' : 'text-secondary';

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <p className="max-w-content-narrow text-sm font-sans text-tertiary">
          Active risk limits for your institution and their open breaches. Breach
          means an open high or critical severity breach, or exposure at or above
          the critical threshold. Warning means an open low or medium severity
          breach, or exposure at or above the warning threshold.
        </p>
      </div>

      <ReadNotice
        subject={SUBJECT}
        failed={outcome === 'failed'}
        refreshFailed={refreshFailed(query)}
        dataUpdatedAt={query.dataUpdatedAt}
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />

      {/* Summary cards, in the badge vocabulary: the kit tile row, two across
          on a phone (UI run r3, fi-cons-13). */}
      <MetricCardGrid className="mb-8">
        {outcome === 'loading' ? (
          <>
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </>
        ) : (
          <>
            <MetricCard
              label="Total Buckets"
              value={data ? String(data.totalBuckets) : '--'}
              caption={withheldCaption}
              description={note('Active risk limit configurations')}
            />
            {/* The route reads a bucket's exposure only from an open breach, so
                a bucket with none is Not measured rather than a GREEN verdict
                on exposure nobody measured. */}
            <MetricCard
              label={BUCKET_STATUS_LABELS.UNKNOWN}
              value={count(notMeasuredBuckets)}
              caption={withheldCaption}
              description={note('No open breach, so no exposure recorded')}
            />
            <MetricCard
              label={BUCKET_STATUS_LABELS.YELLOW}
              value={count(warningBuckets)}
              caption={withheldCaption}
              description={note('Open low or medium breach, or above the warning threshold')}
            />
            <MetricCard
              label={BUCKET_STATUS_LABELS.RED}
              value={count(breachBuckets)}
              caption={withheldCaption}
              description={note('Open high or critical breach, or above the critical threshold')}
            />
          </>
        )}
      </MetricCardGrid>

      {/* Secondary summary row */}
      {data && (
        <div className="flex flex-wrap items-center gap-6 mb-6">
          <div className="flex items-center gap-2">
            {/* ConcentrationRiskResponse.breachingBuckets counts YELLOW and RED
                buckets alike (platform-admin.openapi.yaml), so the strip names
                both. 'Breaching' read as the Breach card's RED-only count. */}
            <span className="text-label-xs font-sans text-tertiary uppercase">Buckets with a warning or breach</span>
            <span className={`font-semibold text-sm ${breachingClass}`}>
              {data.breachingBuckets} of {data.totalBuckets}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-label-xs font-sans text-tertiary uppercase">Open Breaches</span>
            <span className={`font-semibold text-sm ${openBreachClass}`}>
              {totalOpenBreaches}
            </span>
          </div>
        </div>
      )}

      {/* Drill-down table */}
      <div>
        <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-3">
          Risk Limit Buckets
        </div>
        {outcome === 'loading' ? (
          <Skeleton className="h-64" />
        ) : buckets.length > 0 ? (
          <>
            <DataTable columns={CONCENTRATION_COLUMNS} data={buckets} />
            <p className="mt-3 text-xs font-sans text-tertiary">
              Exposure is recorded when a breach is detected. A bucket with no
              open breach has no recorded exposure, so its exposure,
              utilization and breach time show a dash and its status reads Not
              measured.
            </p>
          </>
        ) : (
          <Card>
            <div className="flex items-center justify-center h-24 text-secondary font-sans text-sm text-center px-4">
              {sectionMessage(outcome)}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
